import { useCallback } from 'react';

import {
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';

import {
  mergeClassNameStrings,
  RegistrationAddressT,
} from '@consumer-frontend/nc-utils';
import { Fieldset } from '@consumer-frontend/shared-ui';

import { NcAddressFields } from './NcAddressFields';
import { NcButton } from '../NcButton';
import { NcForm } from '../NcForm';

export type NcAddressFormPropsT = {
  defaultValues: RegistrationAddressT;
  onBack: () => void;
  onSubmit: (address: RegistrationAddressT) => Promise<void>;
  className?: string;
};

export const NcAddressForm = ({
  className,
  defaultValues,
  onBack,
  onSubmit,
}: NcAddressFormPropsT): React.JSX.Element => {
  const methods = useForm<RegistrationAddressT>({
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const { formState: { isSubmitting }, handleSubmit } = methods;

  const handleAddressSubmit = useCallback<SubmitHandler<RegistrationAddressT>>(
    async (formData) => {
      await onSubmit({
        addressLine1: formData.addressLine1.trim(),
        addressLine2: formData.addressLine2?.trim(),
        city: formData.city.trim(),
        postalCode: formData.postalCode.trim(),
        state: formData.state,
      });
    },
    [onSubmit],
  );

  return (
    <FormProvider {...methods}>
      <NcForm
        className={mergeClassNameStrings('address-form', className)}
        onSubmit={handleSubmit(handleAddressSubmit)}
      >
        <NcAddressFields />

        <Fieldset className="cta-buttons">
          <NcButton
            loading={isSubmitting}
            type="submit"
            variant="primary"
          >
            Continue
          </NcButton>

          <NcButton
            disabled={isSubmitting}
            onClick={onBack}
            type="button"
            variant="outline-primary"
          >
            Back
          </NcButton>
        </Fieldset>
      </NcForm>
    </FormProvider>
  );
};
